import React from 'react';
import {useTranslation} from 'react-i18next';

type BaseProps = {
  containerClassName?: string;
  label?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  className?: string;
  px?: string;
  py?: string;
  error?: string;
  labelClassName?: string;
  required?: boolean;
};

type TextInputProps =
  | ({
      multiline?: false;
    } & React.InputHTMLAttributes<HTMLInputElement>)
  | ({
      multiline: true;
    } & React.TextareaHTMLAttributes<HTMLTextAreaElement>);

type Props = BaseProps & TextInputProps;

function AppTextInput({
  containerClassName = '',
  label,
  startIcon,
  endIcon,
  className = '',
  px = 'pl-3 pr-3',
  py = 'py-[10px]',
  multiline,
  error,
  labelClassName,
  required,
  ...rest
}: Props) {
  const {i18n} = useTranslation();
  const isRTL = i18n.language === 'heb' || i18n.dir() === 'rtl';

  // Determine padding adjustments
  const startIconPadding = isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3';
  const endIconPadding = isRTL ? 'pl-10 pr-3' : 'pr-10 pl-3';
  const iconTop = multiline ? (label ? '28px' : '15px') : label ? '75%' : '50%';
  const iconTranslate = `translateY(${
    multiline ? 0 : label ? '-75%' : '-50%'
  })`;
  const styles = `
    w-full
    ${label ? 'pt-[20px] pb-1' : ''}
    ${py}
    ${!startIcon && !endIcon && px}
    ${startIcon ? startIconPadding : ''}
    ${endIcon ? endIconPadding : ''}
    ${
      error
        ? 'border-raspberryRed focus:border-raspberryRed '
        : 'border-lightDarkGray focus:border-tealBlue'
    }
    rounded-[4px]
    border-[1px]
    bg-[#1D1B2014]
    focus:outline-none
    text-[16px] text-black placeholder:text-black
    resize-none
    
    ${className}
  `;

  return (
    <div
      className={`w-full flex flex-col
      ${containerClassName}`}>
      <div className="relative group">
        {label && (
          <span
            className={`text-[12px] font-medium text-[#49454F] absolute top-1 left-2 right-2
              ${error ? 'text-raspberryRed' : 'text-[#49454F]'}
              ${
                error
                  ? 'group-focus-within:text-raspberryRed'
                  : 'group-focus-within:text-tealBlue'
              } 
              ${labelClassName}`}>
            {required && <span className="text-raspberryRed">*</span>}
            {label}
          </span>
        )}
        {multiline ? (
          <textarea
            {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            className={styles}
          />
        ) : (
          <input
            {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
            className={styles}
          />
        )}

        {startIcon && (
          <div
            className={`
              absolute flex items-center justify-center
              ${isRTL ? 'right-3' : 'left-3'}
            `}
            style={{
              top: iconTop,
              transform: iconTranslate,
            }}>
            {startIcon}
          </div>
        )}

        {endIcon && (
          <div
            className={`
              absolute flex items-center justify-center
              ${isRTL ? 'left-3' : 'right-3'}
            `}
            style={{
              top: iconTop,
              transform: iconTranslate,
            }}>
            {endIcon}
          </div>
        )}
      </div>

      {error && <p className="m-0 text-[12px] text-raspberryRed">{error}</p>}
    </div>
  );
}

export default AppTextInput;
